import {
  FunnelDatalayerEvent,
  SharedFunnelDatalayerEvent,
} from 'src/app/_services/types/datalayer.types';
import { Step } from 'src/app/_services/types/step.type';
import {
  BuildingType,
  ConstructionType,
  CoverageType,
  getBuildingTypeLabel,
  getConstructionTypeLabel,
  getHeatingSystemLabel,
  getRoofTypeLabel,
  getSecurityMeasureLabel,
  HeatingSystem,
  RoofType,
  SecurityMeasure,
} from 'src/app/enums/building.enums';
import { LinearStepperElement } from 'src/app/ui-kit/components/linear-stepper/linear-stepper.component';
import { AntennaIconComponent } from 'src/app/ui-kit/icons/antenna-icon.component';
import { AppartmentComplex2IconComponent } from 'src/app/ui-kit/icons/appatment-complex-2-icon.component';
import { BrokenGlassIconComponent } from 'src/app/ui-kit/icons/broken-glass-icon.component';
import { CartwheelIconComponent } from 'src/app/ui-kit/icons/cartwheel-icon.component';
import { CrossIconComponent } from 'src/app/ui-kit/icons/cross-icon.component';
import { EarthquakeIconComponent } from 'src/app/ui-kit/icons/earthquake-icon.component';
import { FireAlarmIconComponent } from 'src/app/ui-kit/icons/fire-alarm-icon.component';
import { FireIconComponent } from 'src/app/ui-kit/icons/fire-icon.component';
import { FlatHouseIconComponent } from 'src/app/ui-kit/icons/house-flat-icon.component';
import { MinimalistHouseIconComponent } from 'src/app/ui-kit/icons/house-minimalist-icon.component';
import { SimpleHouseIconComponent } from 'src/app/ui-kit/icons/house-simple-icon.component';
import { ParkingIconComponent } from 'src/app/ui-kit/icons/parking-icon.component';
import { PipeIconComponent } from 'src/app/ui-kit/icons/pipe-icon.component';
import { OpenSquareIconComponent } from 'src/app/ui-kit/icons/square-open-icon.component';
import { ThiefIconComponent } from 'src/app/ui-kit/icons/thief-icon.component';
import { VillaIconComponent } from 'src/app/ui-kit/icons/villa-icon.component';

import { SelectElementMapper } from '../../_services/types/commons.type';
import { ContractToSignInfo } from '../../_services/types/contract-template.types';
import { ContractType } from '../../enums/contract-type.enum';
import { InsuranceTypeEnum } from '../../enums/insurance-type.enum';

export const OLD_BUILDING_INSURANCE_BASE = 'batiment';
export const BUILDING_INSURANCE_BASE = 'assurance-batiment';
export const BUILDING_INSURANCE_INPUT = 'formulaire';
export const BUILDING_INSURANCE_OFFER = 'offre';

export const BUILDING_INPUT_FORM_BASE = `${BUILDING_INSURANCE_BASE}/${BUILDING_INSURANCE_INPUT}/`;

export const BUILDING_INSURANCE_END = 'fin';
export const BUILDING_INSURANCE_END_URL = `${BUILDING_INPUT_FORM_BASE}fin`;

export const BUILDING_FUNNEL_EVENT_BASE: SharedFunnelDatalayerEvent = {
  event: 'Market_Batiment',
  product_category: 'Batiment',
  product_name: 'Market',
  funnel_complete: false,
};

export const BUILDING_INSURANCE_END_EVENT: FunnelDatalayerEvent = {
  ...BUILDING_FUNNEL_EVENT_BASE,
  funnel_complete: true,
  funnel_stage: 8,
  stage_name: 'Fin',
};

export const BUILDING_INPUT_FORM_STEPS: {
  INSURANCE_FORM: Step;
  BUILDING_FORM: Step;
  COVERS_FORM: Step;
  OFFER_SEARCH: Step;
  LOGIN: Step;
  REGISTER: Step;
  PERSONAL_INFO: Step;
  QUESTIONNARY: Step;
  CANCELLATION: Step;
  END: Step;
} = {
  INSURANCE_FORM: {
    step: [1, 1],
    url: 'preneur-assurance',
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 1,
      stage_name: 'Preneur assurance',
    },
  },
  BUILDING_FORM: {
    step: [2, 1],
    url: 'preneur-assurance',
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 1,
      stage_name: 'Preneur assurance',
    },
  },
  COVERS_FORM: {
    step: [3, 1],
    url: 'couvertures',
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 2,
      stage_name: 'Couvertures',
    },
  },
  OFFER_SEARCH: {
    step: [4, 1],
    url: 'offres',
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 3,
      stage_name: 'Offres',
    },
  },
  LOGIN: {
    step: [4, 1],
    url: 'connexion',
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 4,
      stage_name: 'Connexion',
    },
  },
  REGISTER: {
    step: [4, 1],
    url: 'inscription',
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 4,
      stage_name: 'Inscription',
    },
  },
  PERSONAL_INFO: {
    step: [4, 1],
    url: 'informations-personnelles',
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 5,
      stage_name: 'Informations personnelles',
    },
  },
  QUESTIONNARY: {
    step: [5, 1],
    url: 'questionnaire',
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 6,
      stage_name: 'Questionnaire',
    },
  },
  CANCELLATION: {
    step: [6, 1],
    url: 'resiliation',
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 7,
      stage_name: '(Option) Résiliation',
    },
  },
  END: {
    step: [-1],
    url: 'fin',
    datalayerEvent: BUILDING_INSURANCE_END_EVENT,
  },
};

export const BUILDING_TYPES: SelectElementMapper<BuildingType> = {
  [BuildingType.ATTACHED_HOUSE]: {
    value: BuildingType.ATTACHED_HOUSE,
    label: getBuildingTypeLabel(BuildingType.ATTACHED_HOUSE),
    iconComponent: SimpleHouseIconComponent,
  },
  [BuildingType.FAMILY_HOUSE]: {
    value: BuildingType.FAMILY_HOUSE,
    label: getBuildingTypeLabel(BuildingType.FAMILY_HOUSE),
    iconComponent: MinimalistHouseIconComponent,
  },
  [BuildingType.SECONDARY_RESIDENCE]: {
    value: BuildingType.SECONDARY_RESIDENCE,
    label: getBuildingTypeLabel(BuildingType.SECONDARY_RESIDENCE),
    iconComponent: VillaIconComponent,
  },
  [BuildingType.UNDERGROUND_GARAGE]: {
    value: BuildingType.UNDERGROUND_GARAGE,
    label: getBuildingTypeLabel(BuildingType.UNDERGROUND_GARAGE),
    iconComponent: ParkingIconComponent,
  },
  [BuildingType.APPARTMENT_COMPLEX]: {
    value: BuildingType.APPARTMENT_COMPLEX,
    label: getBuildingTypeLabel(BuildingType.APPARTMENT_COMPLEX),
    iconComponent: AppartmentComplex2IconComponent,
  },
  [BuildingType.OTHER]: {
    value: BuildingType.OTHER,
    label: getBuildingTypeLabel(BuildingType.OTHER),
  },
};

export const SECURITY_MEASURES: SelectElementMapper<SecurityMeasure> = {
  [SecurityMeasure.ALARM]: {
    value: SecurityMeasure.ALARM,
    label: getSecurityMeasureLabel(SecurityMeasure.ALARM),
    iconComponent: FireAlarmIconComponent,
  },
  [SecurityMeasure.NONE]: {
    value: SecurityMeasure.NONE,
    label: getSecurityMeasureLabel(SecurityMeasure.NONE),
    iconComponent: CrossIconComponent,
  },
};

export const HEATING_SYSTEMS: SelectElementMapper<HeatingSystem> = {
  [HeatingSystem.FLOOR_HEATING]: {
    value: HeatingSystem.FLOOR_HEATING,
    label: getHeatingSystemLabel(HeatingSystem.FLOOR_HEATING),
  },
  [HeatingSystem.ROOF_HEATING]: {
    value: HeatingSystem.ROOF_HEATING,
    label: getHeatingSystemLabel(HeatingSystem.ROOF_HEATING),
  },
  [HeatingSystem.FLOOR_ROOF_HEATING]: {
    value: HeatingSystem.FLOOR_ROOF_HEATING,
    label: getHeatingSystemLabel(HeatingSystem.FLOOR_ROOF_HEATING),
  },
  [HeatingSystem.OTHER]: {
    value: HeatingSystem.OTHER,
    label: getHeatingSystemLabel(HeatingSystem.OTHER),
  },
};

export const CONSTRUCTION_TYPES: SelectElementMapper<ConstructionType> = {
  [ConstructionType.MASSIVE]: {
    value: ConstructionType.MASSIVE,
    label: getConstructionTypeLabel(ConstructionType.MASSIVE),
    info: $localize`Généralement, il s'agit des constructions en béton (par exemple une maison à étages ou un immeuble)`,
    iconComponent: OpenSquareIconComponent,
  },
  [ConstructionType.NOT_MASSIVE]: {
    value: ConstructionType.NOT_MASSIVE,
    label: getConstructionTypeLabel(ConstructionType.NOT_MASSIVE),
    info: $localize`Généralement, il s'agit de construction en bois (par exemple un chalet)`,
    iconComponent: CrossIconComponent,
  },
};

export const ROOF_TYPES: SelectElementMapper<RoofType> = {
  [RoofType.FLAT]: {
    value: RoofType.FLAT,
    label: getRoofTypeLabel(RoofType.FLAT),
    iconComponent: FlatHouseIconComponent,
  },
  [RoofType.NOT_FLAT]: {
    value: RoofType.NOT_FLAT,
    label: getRoofTypeLabel(RoofType.NOT_FLAT),
    iconComponent: SimpleHouseIconComponent,
  },
};

export const COVERAGES: SelectElementMapper<CoverageType> = {
  [CoverageType.FIRE_OR_DISASTERS]: {
    value: CoverageType.FIRE_OR_DISASTERS,
    label: $localize`Incendie / événements naturels`,
    iconComponent: FireIconComponent,
    info: $localize` elle couvre les 10 éléments suivants : 
    incendie 
    chutes de pierre 
    hautes eaux
    avalanche 
    pluie 
    inondations
    tempêtes 
    éboulement 
    grêle 
    glissement de terrain`,
  },
  [CoverageType.THEFT]: {
    value: CoverageType.THEFT,
    label: $localize`Vol avec effraction`,
    iconComponent: ThiefIconComponent,
  },
  [CoverageType.WATER_DAMAGE]: {
    value: CoverageType.WATER_DAMAGE,
    label: $localize`Dégâts d'eau`,
    iconComponent: PipeIconComponent,
    info: $localize`Cela englobe tout ce qui concerne les conduites d'eau et les canalisations`,
  },
  [CoverageType.GLASS_BREAKAGE]: {
    value: CoverageType.GLASS_BREAKAGE,
    label: $localize`Bris de glace`,
    iconComponent: BrokenGlassIconComponent,
    info: $localize`Cela englobe tout ce qui est vitrage du bâtiment`,
  },
  [CoverageType.EARTHQUAKE]: {
    value: CoverageType.EARTHQUAKE,
    label: $localize`Tremblement de terre`,
    iconComponent: EarthquakeIconComponent,
  },
  [CoverageType.TECHNICAL_SETUPS]: {
    value: CoverageType.TECHNICAL_SETUPS,
    label: $localize`Installations techniques`,
    iconComponent: AntennaIconComponent,
    info: $localize`englobe les éléments techniques qui sont rattachés au bâtiment (par exemple la ventilation, la climatisation, les panneaux solaires ou la pompe à chaleur)`,
  },
  [CoverageType.EXTERIOR]: {
    value: CoverageType.EXTERIOR,
    label: $localize`Aménagements extérieurs`,
    iconComponent: CartwheelIconComponent,
    info: $localize`Cela englobe le jardin, les buissons, les cabanes extérieurs, mur de soutènement)`,
  },
};

export const CIVIL_RESPONSABILITY_VALUES = [2000000, 3000000, 5000000, 10000000];
export const CIVIL_RESPONSABILITY_OPTIONS = ["2'000'000", "3'000'000", "5'000'000", "10'000'000"];

export const DEDUCTIBLE_VALUES = [0, 200, 500];
export const DEDUCTIBLE_LABELS = ['0', '200', '500'];

export const CONTRACTS_TO_SIGN: ContractToSignInfo[] = [
  {
    name: 'Article 45 bâtiment',
    type: ContractType.ARTICLE_45,
    insuranceType: InsuranceTypeEnum.BUILDING,
  },
  {
    name: 'Mandat gestion bâtiment',
    type: ContractType.MANAGEMENT_AGREEMENT,
    insuranceType: InsuranceTypeEnum.BUILDING,
  },
];

export const BUILDING_META_TITLE = $localize`Assurance Bâtiment en Suisse | Protège ta Propriété | LTY`;

export const BUILDING_META_DESCRIPTION = $localize`Propriétaire ? Protège ta maison ou ton immeuble contre l'incendie, les dégâts d'eau et les risques naturels. Découvre nos solutions d'assurance bâtiment, simples et claires.`;

export const BUILDING_STEPS_NAMES: LinearStepperElement[] = [
  {
    title: $localize`Preneur d'assurance`,
  },
  {
    title: $localize`Sélection du bien`,
  },
  {
    title: $localize`Couvertures`,
  },
  {
    title: $localize`Compte`,
  },
  {
    title: $localize`Questionnaire`,
  },
  {
    title: $localize`Résiliation (optionnelle)`,
  },
];

// eslint-disable-next-line no-irregular-whitespace
export const BUILDING_TYPE_HELP_TEXT_TITLE = $localize`En quoi le type de bâtiment est-il important pour mon assurance ?`;

export const BUILDING_TYPE_HELP_TEXT_CONTENT = $localize`Spécifier le type de bâtiment permet à l'assureur de comprendre les risques auxquels le bâtiment est exposé et de proposer une couverture adéquate pour protéger ta propriété.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUILDING_INSURANCE_SUM_HELP_TITLE = $localize`Qu'est-ce qu'une somme d'assurance pour mon assurance bâtiment ?`;

export const BUILDING_INSURANCE_SUM_HELP_CONTENT = $localize`La somme d'assurance correspond à la valeur estimée au moment de la souscription du bâtiment que tu souhaite assurer. Il est important que ce montant reflète la réalité à l'instant actuel ; si la somme est sous-évaluée, en cas de sinistre tu ne percevras pas l'entier de la somme des dommages. Si la somme est surévaluée, tu paieras des primes plus importantes.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUILDING_SECURITY_MEASURE_HELP_TITLE = $localize`En quoi le fait d'avoir une alarme incendie change quelque chose pour mon assurance bâtiment ?`;

export const BUILDING_SECURITY_MEASURE_HELP_CONTENT = $localize`Savoir si tu possèdes une alarme incendie dans ton bâtiment est important car cette mesure peut réduire les risques et les pertes en cas de sinistre. Dans ce cas, par exemple, la présence d'une alarme incendie peut entraîner des réductions de prime ou des couvertures étendues pour certains types de sinistres.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUILDING_HEATING_HELP_TITLE = $localize`En quoi le sytème de chauffage influence-t-il mon assurance bâtiment ?`;

export const BUILDING_HEATING_HELP_CONTENT = $localize`Le type de système de système de chauffage dans un bâtiment peut avoir un impact sur le prix de l'assurance en raison de ses implications en termes de risques d'incendie, de risques environnementaux, de maintenance et de conformité aux normes de sécurité.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUILDING_CONSTRUCTION_TYPE_HELP_TITLE = $localize`En quoi le genre de construction est-il important ?`;

export const BUILDING_CONSTRUCTION_TYPE_HELP_CONTENT = $localize`Le genre de construction, qu'elle soit massive ou non massive, peut influencer le coût de l'assurance en raison de ses implications sur la résistance aux dommages et sur les coûts de réparation potentiels. Les assureurs prendront en compte ces facteurs lors de l'évaluation du risque et de la tarification de l'assurance pour un bâtiment spécifique.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUILDING_ROOF_TYPE_HELP_TITLE = $localize`En quoi le type de toit est-il important ?`;

export const BUILDING_ROOF_TYPE_HELP_CONTENT = $localize`Il s'agit ici d'évaluer les potentiels risques de chaque type de toit ; un toit plat a l'inconvénient potentiel d'être plus vulnérable aux fuites d'eau là ou un toit en pente a l'inconvénient d'être plus vulnérable aux fortes rafales de vent. Les assureurs prendront en compte ces facteurs lors de l'évaluation du risque et de la tarification pour s'assurer que le bâtiment est correctement couvert.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUILDING_CIVIL_RESPONSABILITY_HELP_TITLE = $localize`Pourquoi devrais-je souscrire à une assurance responsabilité civile pour le bâtiment ?`;

export const BUILDING_CIVIL_RESPONSABILITY_HELP_CONTENT = $localize`La responsabilité civile pour ton bâtiment te protège si quelque chose se passe sur ta propriété et/ou que quelqu'un d'autre est blessé ou que ses biens sont endommagés (par exemple la tuile qui tombe de ton toit et qui atterit sur la voiture de ton voisin).
Cette option est importante, parce qu'elle te protège contre les risques financiers suite à un sinistre et te permet de dormir sur tes deux oreilles. Plus le curseur ci-dessus sera haut, mieux tu pourras dormir la nuit.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUILDING_DEDUCTIBLE_HELP_TITLE = $localize`En quoi la franchise est importante pour la détermination de la prime pour ma RC Bâtiment ?`;

export const BUILDING_DEDUCTIBLE_HELP_CONTENT = $localize`La franchise est le montant que tu t'engages à payer avant que l'assureur ne prenne en charge les frais de ton sinistre. Plus la franchise est haute, moins la prime le sera et vice versa. A toi de choisir le montant qui te convient par rapport à ton budget.`;

export const END_SCREEN_TEXTS: LinearStepperElement[] = [
  {
    title: $localize`Tu vas recevoir un comparatif d'offres (dans les 24h ouvrées)`,
  },
  {
    title: $localize`Tu valides dans ton espace client la proposition choisie`,
  },
  {
    title: $localize`Ta police est en attente de validation (3 à 10 jours)`,
  },
  {
    title: $localize`Ta police est créée et accessible dans ton Wallet`,
  },
  {
    title: $localize`Si tu nous a confié la résiliation de ta/tes polices actuelle(s), nous envoyons le(s) courrier(s) de résiliation précedemment validé(s)`,
  },
];
